/**
 * The two admin-only write dialogs — bonus grant and bonus reversal.
 *
 * Both implement exactly the submission contract in `submission.ts` (design
 * D12): one idempotency key per dialog session, validation once before the first
 * submission, every field locked the moment a submission is sent, and the
 * server's own answer rendered rather than predicted. Neither dialog holds any
 * state that outlives its closing.
 *
 * grant-dialog-monetary-bonus-target adds, on top of that unchanged contract:
 *
 * - **monetary targets** (D3): both asset kinds are offered, monetary group
 *   first, every option naming its kind, currency and `spending_priority` —
 *   `bonus_cash` (monetary) and `bonus_usable` (virtual) were indistinguishable
 *   in the shipped dropdown, which is the proximate cause of the incident;
 * - **no preselection before the catalogue's first `200`** (D4): the default is
 *   the smallest-`spending_priority` monetary bucket, a rule whose inputs live
 *   only in the catalogue, so a catalogue-free stand-in would have frozen the
 *   first bucket *by name* forever;
 * - **major-unit entry** for a monetary target (D5), converted by decimal-string
 *   / `BigInt` arithmetic, with the exact integer echoed before submit;
 * - **an acknowledgement** that 回收赠送 can never reclaim a monetary grant (D6),
 *   whose reclaim sentence is the reversal dialog's own Path B predicate;
 * - **a directed monetary reclaim path** in the reversal dialog (D7).
 *
 * Built from the existing dialog / input / select / button components — no
 * native `<form>` submission, so nothing can post a payload the state machine
 * did not authorize.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  AMOUNT_MAX,
  amountUnitLabel,
  describeWireAmount,
  formatAmount,
  majorUnitExponent,
  parseMajorUnitInput,
  validateAmountInput,
} from './amounts'
import { formatExpiryLocal, serializeExpiry } from './expiry'
import {
  applyOutcome,
  beginRetry,
  beginSubmit,
  classifyHttpOutcome,
  closeDialog,
  failValidation,
  generateIdempotencyKey,
  openDialog,
  type DialogState,
} from './submission'
import {
  createSessionController,
  openSessionEffect,
  sameTarget,
  sendInSession,
  type SessionTarget,
} from './dialogSession'
import {
  DIRECTED_DEBIT_INSUFFICIENT_MESSAGE,
  describeDirectedDebit,
  describeReversal,
} from './reversalOutcome'
import {
  errorCode,
  errorStatus,
  fetchSubAccountTypes,
  serverErrorMessage,
  submitDirectedDebit,
  submitGrant,
  submitProvisionSubAccount,
  submitReversal,
  type ReversalOutcome,
  type SubAccountRow,
  type SubAccountTypeEnvelope,
  type SubAccountTypeRow,
} from './accountsApi'
import {
  MONETARY_LEDGER_MARKER_NOTE,
  MONETARY_NO_EVENT_NOTE,
  VIRTUAL_DOWNSTREAM_NOTE,
  buildGrantTargets,
  buildReclaimTargets,
  canSubmitGrant,
  defaultGrantTarget,
  describeGrantTarget,
  isReclaimableFromDialog,
  reclaimStatement,
  requiresMonetaryAcknowledgement,
  targetAsset,
  type GrantTarget,
} from './grantTargets'
import {
  buildProvisionOptions,
  canSubmitProvision,
  resolveProvisionStep,
  shouldRequestCatalogue,
  type CatalogueState,
  type ProvisionMode,
  type ProvisionOption,
} from './provisionOptions'
import {
  beginConfirming,
  beginProvision,
  confirmProvision,
  failConfirmation,
  failProvision,
  initialProvisionState,
  resolveProvisionedTarget,
  retryConfirmation,
  type DetailReloadResult,
  type ProvisionState,
} from './provisionSession'

export interface WriteDialogProps {
  open: boolean
  applicationId: number
  accountId: string
  onClose: () => void
  /** Refresh balances and ledger from the server after an accepted outcome. */
  onRefresh: () => void
  notify: (message: string) => void
}

const CATALOGUE_FAILED_PREFIX = '无法读取该应用的桶类型'
const PRIORITIES_UNAVAILABLE = '桶的消费优先级未能加载，下拉项暂不显示优先级。'

/**
 * One dialog opening: its submission state machine plus its session identity.
 *
 * round-1 fix (F1/F2/F10): the controller is the single authority on whether an
 * async answer may still act. It is invalidated on close and on unmount, and a
 * new token is minted only when the dialog is opened again — so a response that
 * arrives after either event finds no current session and is discarded whole.
 *
 * round-2 fix (F1): opening is an **effect**, symmetric with the cleanup that
 * invalidates. Opening during render while invalidating in a cleanup left the
 * component sessionless under StrictMode (mount → cleanup → mount, with no
 * re-render in between to re-open), which silently disabled every request the
 * dialog had already issued. The session id is published as state so that
 * per-session work can depend on it rather than on a one-shot ref.
 */
function useDialogSession(open: boolean, applicationId: number, accountId: string) {
  const [state, setState] = useState<DialogState>(() => openDialog(generateIdempotencyKey))
  const controllerRef = useRef(createSessionController())
  const closedRef = useRef(false)
  const [sessionId, setSessionId] = useState<number | null>(null)
  const controller = controllerRef.current

  useEffect(() => {
    if (!open) {
      // The parent has actually closed us; the next opening may start a session.
      closedRef.current = false
      return undefined
    }
    // `close()` already ended this opening: do not resurrect it while the
    // parent is still rendering us with `open`.
    if (closedRef.current) return undefined
    return openSessionEffect(controller, applicationId, accountId, setSessionId)
  }, [accountId, applicationId, controller, open])

  const reset = useCallback(() => setState(openDialog(generateIdempotencyKey)), [])
  const endSession = useCallback(() => {
    controller.invalidate()
    closedRef.current = true
    setSessionId(null)
  }, [controller])

  return { state, setState, reset, controller, endSession, sessionId }
}

/**
 * Close the dialog when the account it was opened against changes underneath
 * it (browser back/forward on the reused account-detail route).
 *
 * round-2 fix (F1): the guard now compares the props against the target this
 * dialog last saw, instead of against the live session token — the session is
 * re-opened for the new target by `useDialogSession`, so a token comparison
 * would never see the change. Replaying this effect is a no-op: the ref was
 * already advanced, exactly like the page's own `dialogTargetRef`.
 */
function useCloseOnTargetChange(
  applicationId: number,
  accountId: string,
  close: () => void
) {
  const seen = useRef<SessionTarget>({ applicationId, accountId })
  useEffect(() => {
    if (sameTarget(seen.current, { applicationId, accountId })) return
    seen.current = { applicationId, accountId }
    close()
  }, [accountId, applicationId, close])
}

/**
 * The type catalogue, fetched **whenever the dialog opens** (design D2): the
 * grant form reads its targets' `spending_priority` and `withdrawable` from it,
 * not only the provisioning step.
 *
 * One request per SESSION (round-2 fix F1): a session that dies before its
 * answer arrives — StrictMode's mount/cleanup/mount replay, a close, a
 * navigation — takes its discarded request with it, and the session that
 * replaces it issues its own. A boolean marker would leave the dialog waiting
 * on an answer `sendInSession` can no longer apply.
 */
function useTypeCatalogue(
  open: boolean,
  sessionId: number | null,
  controller: ReturnType<typeof createSessionController>
) {
  const [catalogue, setCatalogue] = useState<CatalogueState>({ status: 'loading' })
  const requestedFor = useRef<number | null>(null)

  const load = useCallback(async () => {
    const token = controller.current()
    if (token === null) return
    setCatalogue({ status: 'loading' })
    await sendInSession<SubAccountTypeEnvelope>({
      token,
      controller,
      request: (target: SessionTarget) => fetchSubAccountTypes(target.applicationId),
      onSuccess: (envelope: SubAccountTypeEnvelope) =>
        setCatalogue({ status: 'loaded', types: envelope.sub_account_types }),
      onFailure: (err: unknown) =>
        setCatalogue({
          status: 'failed',
          message: serverErrorMessage(err, CATALOGUE_FAILED_PREFIX),
        }),
    })
  }, [controller])

  useEffect(() => {
    if (!shouldRequestCatalogue({ open, sessionId, requestedForSession: requestedFor.current }))
      return
    requestedFor.current = sessionId
    void load()
  }, [load, open, sessionId])

  /** The catalogue rows, or `null` while unresolved or failed (fail closed). */
  const types: readonly SubAccountTypeRow[] | null =
    catalogue.status === 'loaded' ? catalogue.types : null

  return { catalogue, setCatalogue, types, reload: load }
}

function OutcomeControls({
  state,
  onRetry,
  onClose,
  onSubmit,
  submitLabel,
  submitDisabled,
}: {
  state: DialogState
  onRetry: () => void
  onClose: () => void
  onSubmit: () => void
  submitLabel: string
  submitDisabled: boolean
}) {
  if (state.status === 'unknown') {
    return (
      <>
        <Button variant="outline" onClick={onRetry}>Retry unchanged</Button>
        <Button onClick={onClose}>Close and check the ledger</Button>
      </>
    )
  }
  if (state.status === 'rejected' || state.status === 'indeterminate') {
    return <Button onClick={onClose}>Close</Button>
  }
  return (
    <>
      <Button variant="outline" onClick={onClose} disabled={state.locked}>Cancel</Button>
      <Button onClick={onSubmit} disabled={submitDisabled || state.locked}>
        {submitLabel}
      </Button>
    </>
  )
}

/**
 * Validate the amount **in the unit the selected target dictates** — the one
 * place that decision is made for either dialog.
 *
 * A `null` exponent (a virtual bucket, or a monetary bucket whose currency the
 * console's table does not contain) is minor-unit integer entry; a known
 * exponent is major-unit entry converted by decimal-string arithmetic. No
 * `Number` is involved on either branch.
 */
function validateForTarget(
  raw: string,
  bucket: { asset_kind: string; currency: string | null } | null
): { ok: true; value: string } | { ok: false; message: string } {
  if (bucket === null) return { ok: false, message: '请先选择目标子账户。' }
  const exponent = majorUnitExponent(bucket)
  const result =
    exponent === null ? validateAmountInput(raw) : parseMajorUnitInput(raw, exponent)
  return result.ok ? { ok: true, value: result.value } : { ok: false, message: result.message }
}

// ---------------------------------------------------------------------------
// Grant
// ---------------------------------------------------------------------------

interface GrantPayloadSnapshot {
  subAccountId: string
  amount: string
  reason: string
  expiresAt: string | null
  /** The target's own asset shape, for rendering the outcome in its unit. */
  asset: { asset_kind: string; currency: string | null }
}

export interface GrantDialogProps extends WriteDialogProps {
  /**
   * The grant targets: every `active` bucket the account holds, of **both**
   * asset kinds (grant-dialog-monetary-bonus-target D3). A frozen or closed
   * bucket is a target the server would refuse, and an account holding only
   * such a bucket must still reach the provisioning step.
   */
  activeBuckets: readonly SubAccountRow[]
  /** Every bucket the account holds, in any status: the held-type subtraction. */
  heldSubAccounts: readonly SubAccountRow[]
  /**
   * Reload the account detail row from the server (the page's own `loadDetail`).
   * The ONLY source of what the console renders after a provisioning: nothing
   * is inferred from the provisioning response body (design D6).
   */
  reloadDetail: () => Promise<DetailReloadResult>
}

export function GrantDialog(props: GrantDialogProps) {
  const {
    open, applicationId, accountId, activeBuckets, heldSubAccounts,
    reloadDetail, onClose, onRefresh, notify,
  } = props
  const { state, setState, reset, controller, endSession, sessionId } = useDialogSession(
    open, applicationId, accountId
  )
  // NO preselection (design D4): the default is a function of the catalogue's
  // priorities, so the selector stands empty until the catalogue's first 200.
  const [subAccountId, setSubAccountId] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [expiry, setExpiry] = useState('')
  const [monetaryAcknowledged, setMonetaryAcknowledged] = useState(false)
  const [statementChanged, setStatementChanged] = useState(false)
  const locked = useRef<GrantPayloadSnapshot | null>(null)

  const { catalogue, types, reload: loadCatalogue } = useTypeCatalogue(
    open, sessionId, controller
  )
  /** The one-time default has been applied (or deliberately skipped) already. */
  const defaultApplied = useRef(false)
  /** The reclaim sentence the current acknowledgement was given under. */
  const statementShown = useRef<string | null>(null)

  // --- The provisioning step: its own submission state, sharing nothing with
  // the grant's `DialogState` or its idempotency key. ---
  const [typeSelection, setTypeSelection] = useState('')
  const [provisionAcknowledged, setProvisionAcknowledged] = useState(false)
  const [provision, setProvision] = useState<ProvisionState>(initialProvisionState)
  /** True once the operator followed the grant form's monetary entry point. */
  const [monetaryEntry, setMonetaryEntry] = useState(false)

  const targetSet = useMemo(
    () => buildGrantTargets(activeBuckets, types),
    [activeBuckets, types]
  )
  const selectedTarget: GrantTarget | null =
    targetSet.targets.find((target) => target.id === subAccountId) ?? null

  /**
   * The ONE place the selected target changes (design D5, §4.5).
   *
   * Its subject is the *selection*, not the operator: the catalogue's one-time
   * preselection and the preselection of a newly provisioned bucket go through
   * it too, because the digits on screen were typed against the OLD target's
   * unit either way. Reinterpreting them under the new unit is the 100×-class
   * error this whole change exists to prevent. The reason and the expiry are
   * unit-independent and survive untouched.
   */
  const selectTarget = useCallback((nextId: string) => {
    setSubAccountId(nextId)
    setAmount('')
    setMonetaryAcknowledged(false)
    setStatementChanged(false)
    statementShown.current = null
  }, [])

  const close = useCallback(() => {
    // Invalidate FIRST: from here no in-flight answer may touch this component
    // or the parent, whatever it turns out to be.
    endSession()
    setState(closeDialog)
    locked.current = null
    setSubAccountId('')
    setAmount('')
    setReason('')
    setExpiry('')
    setMonetaryAcknowledged(false)
    setStatementChanged(false)
    statementShown.current = null
    defaultApplied.current = false
    setProvision(initialProvisionState)
    setTypeSelection('')
    setProvisionAcknowledged(false)
    setMonetaryEntry(false)
    reset()
    onClose()
  }, [endSession, onClose, reset, setState])

  // The route reuses this component across account ids (browser back/forward):
  // a dialog may never outlive the target it was opened against.
  useCloseOnTargetChange(applicationId, accountId, close)

  /**
   * The one-time default preselection, on the catalogue's FIRST `200` (D4).
   *
   * Skipped entirely when the operator has already selected a target by hand
   * (an explicit choice outranks a default) and once a submission has locked the
   * fields. Every later answer — a retry, a second listing — re-orders and
   * re-labels only; it never selects, re-selects, or clears.
   */
  useEffect(() => {
    if (catalogue.status !== 'loaded') return
    if (defaultApplied.current) return
    defaultApplied.current = true
    if (state.locked) return
    if (subAccountId) return
    const next = defaultGrantTarget(targetSet)
    if (next) selectTarget(next)
  }, [catalogue.status, selectTarget, state.locked, subAccountId, targetSet])

  const reclaimable = isReclaimableFromDialog(selectedTarget, activeBuckets, types)
  const statement = requiresMonetaryAcknowledgement(selectedTarget)
    ? reclaimStatement(reclaimable)
    : null

  /**
   * The one thing a late catalogue invalidates: an acknowledgement whose text
   * it changed (§4.6). An unchanged statement leaves it valid; the amount, the
   * reason and the expiry are untouched either way.
   */
  useEffect(() => {
    if (state.locked) return
    if (statement === null) {
      statementShown.current = null
      return
    }
    if (statementShown.current !== null && statementShown.current !== statement) {
      setMonetaryAcknowledged(false)
      setStatementChanged(true)
    }
    statementShown.current = statement
  }, [statement, state.locked])

  const send = useCallback(
    async (payload: GrantPayloadSnapshot, isRetry: boolean) => {
      const token = controller.current()
      if (token === null) return
      await sendInSession({
        token,
        controller,
        // The frozen target of THIS opening — never the live props.
        request: (target: SessionTarget) =>
          submitGrant(target.applicationId, target.accountId, payload.subAccountId, {
            amount: payload.amount,
            reason: payload.reason,
            idempotencyKey: state.idempotencyKey,
            expiresAt: payload.expiresAt,
          }),
        onSuccess: () => {
          const result = applyOutcome(state, { kind: 'success' }, { isRetry })
          setState(result.state)
          if (result.effects.includes('success-toast')) {
            // Reported in the TARGET's own rendering, not as a bare integer.
            notify(`已赠送 ${formatAmount(payload.amount, payload.asset)}`)
          }
          if (result.effects.includes('refresh')) onRefresh()
          close()
        },
        onFailure: (err: unknown) => {
          const outcome = classifyHttpOutcome(
            errorStatus(err),
            serverErrorMessage(err, 'Grant failed')
          )
          setState((current) => applyOutcome(current, outcome, { isRetry }).state)
        },
      })
    },
    [close, controller, notify, onRefresh, setState, state]
  )

  const submit = useCallback(() => {
    // Validation runs exactly once, before the first request is sent.
    if (!selectedTarget) {
      setState((current) => failValidation(current, '请先选择目标子账户。'))
      return
    }
    const asset = targetAsset(selectedTarget)
    const amountCheck = validateForTarget(amount, asset)
    if (!amountCheck.ok) {
      setState((current) => failValidation(current, amountCheck.message))
      return
    }
    if (!reason.trim()) {
      setState((current) => failValidation(current, 'A reason is required.'))
      return
    }
    if (requiresMonetaryAcknowledgement(selectedTarget) && !monetaryAcknowledged) {
      setState((current) =>
        failValidation(current, '选择 monetary 桶时，必须先勾选「不可用回收赠送收回」的确认。')
      )
      return
    }
    let expiresAt: string | null = null
    if (expiry) {
      const serialized = serializeExpiry(expiry)
      if (!serialized.ok) {
        setState((current) => failValidation(current, serialized.message))
        return
      }
      expiresAt = serialized.value
    }
    const payload: GrantPayloadSnapshot = {
      subAccountId: selectedTarget.id,
      amount: amountCheck.value,
      reason: reason.trim(),
      expiresAt,
      asset,
    }
    locked.current = payload
    setState(beginSubmit)
    void send(payload, false)
  }, [amount, expiry, monetaryAcknowledged, reason, selectedTarget, send, setState])

  const retry = useCallback(() => {
    const payload = locked.current
    if (!payload) return
    setState(beginRetry)
    void send(payload, true)
  }, [send, setState])

  const hasBucket = activeBuckets.length > 0
  const hasActiveMonetary = activeBuckets.some(
    (bucket) => bucket.asset_kind === 'monetary' && bucket.status === 'active'
  )

  // -------------------------------------------------------------------------
  // Provisioning step
  // -------------------------------------------------------------------------

  const provisionMode: ProvisionMode = hasBucket ? 'monetary' : 'any'
  const options = useMemo(
    () =>
      buildProvisionOptions(
        catalogue.status === 'loaded' ? catalogue.types : [],
        heldSubAccounts,
        provisionMode
      ),
    [catalogue, heldSubAccounts, provisionMode]
  )
  const step = useMemo(
    () => resolveProvisionStep(catalogue, options, provisionMode),
    [catalogue, options, provisionMode]
  )
  const selectedOption: ProvisionOption | undefined =
    step.kind === 'options'
      ? step.options.find((option) => option.typeKey === typeSelection)
      : undefined
  const provisionInFlight =
    provision.status === 'submitting' || provision.status === 'confirming'
  const canProvision = canSubmitProvision(typeSelection || null, provisionAcknowledged, {
    step,
    phase: provision.status,
  })

  /** The post-2xx confirmation: only the reloaded row decides (design D6). */
  const confirmProvisioned = useCallback(
    async (typeKey: string, assetKind: string) => {
      const token = controller.current()
      if (token === null) return
      const result = await reloadDetail()
      if (!controller.isCurrent(token)) return
      if (!result.ok) {
        setProvision((current) =>
          failConfirmation(
            current,
            `子账户已开通，但刷新账户失败：${result.message}`
          )
        )
        return
      }
      const target = resolveProvisionedTarget(result.row, typeKey, assetKind)
      if (target === null) {
        setProvision((current) =>
          failConfirmation(current, '子账户已开通，但该桶当前不可用于赠送。')
        )
        return
      }
      // §4.8: the preselection goes through the SAME selection-change path as
      // an operator's, so the amount is cleared and re-labelled in the new
      // target's unit and a monetary target re-requires the acknowledgement.
      // The reason and the expiry are preserved.
      selectTarget(target)
      setMonetaryEntry(false)
      setProvision((current) => confirmProvision(current, target))
    },
    [controller, reloadDetail, selectTarget]
  )

  const sendProvision = useCallback(
    async (option: ProvisionOption) => {
      const token = controller.current()
      if (token === null) return
      await sendInSession<void>({
        token,
        controller,
        request: (target: SessionTarget) =>
          submitProvisionSubAccount(target.applicationId, target.accountId, {
            typeKey: option.typeKey,
            displayName: option.displayName,
            assetKind: option.assetKind,
            currency: option.currency,
          }),
        onSuccess: () => {
          setProvision((current) => beginConfirming(current))
          void confirmProvisioned(option.typeKey, option.assetKind)
        },
        onFailure: (err: unknown) =>
          setProvision((current) =>
            failProvision(current, serverErrorMessage(err, '开通子账户失败'))
          ),
      })
    },
    [confirmProvisioned, controller]
  )

  const submitProvision = useCallback(() => {
    if (!selectedOption || !canProvision) return
    // The type AND its asset kind are captured HERE, at send time: no later
    // decision re-reads the selector.
    const begun = beginProvision(provision, selectedOption.typeKey, selectedOption.assetKind)
    if (!begun.ok) return
    setProvision(begun.state)
    void sendProvision(selectedOption)
  }, [canProvision, provision, selectedOption, sendProvision])

  const retryRefresh = useCallback(() => {
    const next = retryConfirmation(provision)
    if (!next.ok || next.state.typeKey === null) return
    setProvision(next.state)
    void confirmProvisioned(next.state.typeKey, next.state.assetKind ?? '')
  }, [confirmProvisioned, provision])

  // The grant form shows when the account already has an active bucket and
  // nothing was provisioned, or once a provisioning has been CONFIRMED by the
  // server's own reloaded row. The monetary entry point puts the operator on
  // the step deliberately, from a grant form that stays reachable behind it.
  const onProvisionStep =
    provision.status !== 'confirmed' &&
    (!hasBucket || monetaryEntry || provision.status !== 'idle')

  /** The monetary provisioning entry point's own precondition (design D8). */
  const monetaryEntryAvailable =
    hasBucket &&
    !hasActiveMonetary &&
    options.some((option) => option.assetKind === 'monetary')

  const canSubmitTheGrant = canSubmitGrant(selectedTarget, monetaryAcknowledged, {
    locked: state.locked,
  })

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>赠送余额</DialogTitle>
          <DialogDescription>
            可赠送到该账户处于 active 状态的 monetary 或 virtual 子账户；
            发送到服务端的金额始终是该桶最小单位的正整数，上限 {AMOUNT_MAX}。
          </DialogDescription>
        </DialogHeader>

        {onProvisionStep ? (
          <div className="space-y-3">
            {provision.status === 'unconfirmed' ? (
              <p role="alert" className="text-sm text-amber-700">{provision.message}</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {hasBucket
                    ? '该账户还没有处于 active 状态的 monetary 子账户，可在此先开通一个。'
                    : '该账户没有处于 active 状态的子账户，可在此先开通一个。'}
                </p>
                {step.kind === 'loading' && (
                  <p className="text-sm text-muted-foreground">
                    正在读取该应用的桶类型…
                  </p>
                )}
                {step.kind === 'failed' && (
                  <p role="alert" className="text-sm text-red-700">
                    {CATALOGUE_FAILED_PREFIX}：{step.message}
                  </p>
                )}
                {step.kind === 'no-types' && (
                  <p className="text-sm text-amber-700">
                    该应用尚未配置任何桶类型。需先通过
                    {' '}<code>POST /api/v1/accounts/&lt;account_id&gt;/sub-accounts</code>{' '}
                    以一个新的 type_key 创建一个桶类型，本页暂不提供类型配置入口。
                  </p>
                )}
                {step.kind === 'no-monetary-types' && (
                  <p className="text-sm text-amber-700">
                    该应用尚未配置任何 monetary 桶类型（已配置的都是 virtual）。需先通过
                    {' '}<code>POST /api/v1/accounts/&lt;account_id&gt;/sub-accounts</code>{' '}
                    以一个新的 type_key 创建一个 monetary 类型，本页暂不提供类型配置入口。
                  </p>
                )}
                {step.kind === 'all-held' && (
                  <p className="text-sm text-amber-700">
                    该账户已持有本应用每一种已配置桶类型的子账户，且其中没有一个处于
                    active 状态。解冻或重开子账户不在本功能范围内。
                  </p>
                )}
                {step.kind === 'options' && (
                  <>
                    <label className="block text-sm">
                      <span className="mb-1 block text-muted-foreground">桶类型</span>
                      <select
                        className="w-full rounded-md border px-3 py-2"
                        value={typeSelection}
                        onChange={(e) => {
                          setTypeSelection(e.target.value)
                          setProvisionAcknowledged(false)
                        }}
                        disabled={provisionInFlight}
                        aria-label="桶类型"
                      >
                        <option value="">请选择</option>
                        {step.options.map((option) => (
                          <option key={option.typeKey} value={option.typeKey}>
                            {option.displayName}（{option.typeKey}） ·{' '}
                            {option.assetKind}
                            {option.currency ? ` · ${option.currency}` : ''} · 优先级{' '}
                            {option.spendingPriority}
                            {option.acknowledgementRequired ? ' · 不可回收' : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedOption?.acknowledgementRequired && (
                      <label className="flex items-start gap-2 text-sm text-amber-700">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={provisionAcknowledged}
                          onChange={(e) => setProvisionAcknowledged(e.target.checked)}
                          disabled={provisionInFlight}
                          aria-label="确认该桶不可回收"
                        />
                        <span>
                          该类型不可回收：开通后可以赠送，但「回收赠送」永远无法从该桶收回余额。
                          我已知悉并确认开通。
                        </span>
                      </label>
                    )}
                  </>
                )}
                {provision.status === 'failed' && provision.message && (
                  <p role="alert" className="text-sm text-red-700">{provision.message}</p>
                )}
                {provisionInFlight && (
                  <p className="text-sm text-muted-foreground">正在开通…</p>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">目标子账户</span>
              <select
                className="w-full rounded-md border px-3 py-2"
                value={subAccountId}
                onChange={(e) => selectTarget(e.target.value)}
                disabled={state.locked}
                aria-label="目标子账户"
              >
                <option value="">请选择目标子账户</option>
                {targetSet.targets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {describeGrantTarget(target)}
                  </option>
                ))}
              </select>
            </label>
            {catalogue.status === 'loading' && (
              <p className="text-sm text-muted-foreground">
                正在读取桶的消费优先级…（可直接选择目标）
              </p>
            )}
            {catalogue.status === 'failed' && (
              <p role="alert" className="text-sm text-amber-700">
                {PRIORITIES_UNAVAILABLE}
                {' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => { void loadCatalogue() }}
                >
                  重试
                </button>
              </p>
            )}
            {monetaryEntryAvailable && !state.locked && (
              <p className="text-sm text-muted-foreground">
                该账户还没有 monetary 桶。
                {' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => setMonetaryEntry(true)}
                >
                  开通一个 monetary 桶
                </button>
              </p>
            )}
            {selectedTarget && selectedTarget.assetKind !== 'monetary' && (
              <p className="text-sm text-amber-700">{VIRTUAL_DOWNSTREAM_NOTE}</p>
            )}
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">
                {amountUnitLabel(selectedTarget ? targetAsset(selectedTarget) : null)}
              </span>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                readOnly={state.locked}
                disabled={selectedTarget === null}
                inputMode="decimal"
                placeholder={selectedTarget === null ? '先选择目标' : '例如 10.00'}
                aria-label="金额"
              />
              {selectedTarget && amount !== '' && (() => {
                const check = validateForTarget(amount, targetAsset(selectedTarget))
                return check.ok ? (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {describeWireAmount(check.value, targetAsset(selectedTarget))}
                  </span>
                ) : null
              })()}
            </label>
            {statement && (
              <label className="flex items-start gap-2 text-sm text-amber-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={monetaryAcknowledged}
                  onChange={(e) => setMonetaryAcknowledged(e.target.checked)}
                  disabled={state.locked}
                  aria-label="确认 monetary 赠金不可用回收赠送收回"
                />
                <span>
                  {statement} {MONETARY_LEDGER_MARKER_NOTE} {MONETARY_NO_EVENT_NOTE}
                  {statementChanged ? ' 该说明已因桶类型目录更新而变化，请重新确认。' : ''}
                  {' '}我已知悉并确认赠送。
                </span>
              </label>
            )}
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">原因（必填，写入流水）</span>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                readOnly={state.locked}
                aria-label="原因"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">过期时间（可选，本地时区）</span>
              <Input
                type="datetime-local"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                readOnly={state.locked}
                aria-label="过期时间"
              />
              {expiry && (
                <span className="mt-1 block text-xs text-muted-foreground">
                  将于 {formatExpiryLocal(serializeExpiryPreview(expiry) ?? expiry)} （本地时间）过期
                </span>
              )}
            </label>
          </div>
        )}

        {state.message && (
          <p role="alert" className="text-sm text-red-700">{state.message}</p>
        )}

        <DialogFooter>
          {onProvisionStep ? (
            provision.status === 'unconfirmed' ? (
              <>
                <Button variant="outline" onClick={retryRefresh}>重试刷新</Button>
                <Button onClick={close}>关闭</Button>
              </>
            ) : (
              <>
                {hasBucket && monetaryEntry && provision.status === 'idle' ? (
                  <Button
                    variant="outline"
                    onClick={() => setMonetaryEntry(false)}
                    disabled={provisionInFlight}
                  >
                    返回赠送
                  </Button>
                ) : (
                  <Button variant="outline" onClick={close} disabled={provisionInFlight}>
                    关闭
                  </Button>
                )}
                {step.kind === 'failed' && (
                  <Button variant="outline" onClick={() => { void loadCatalogue() }}>
                    重试
                  </Button>
                )}
                <Button onClick={submitProvision} disabled={!canProvision}>
                  开通子账户
                </Button>
              </>
            )
          ) : (
            <OutcomeControls
              state={state}
              onRetry={retry}
              onClose={close}
              onSubmit={submit}
              submitLabel="确认赠送"
              submitDisabled={!canSubmitTheGrant}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Preview helper: the UTC instant the dialog would send, or null if unparseable. */
function serializeExpiryPreview(raw: string): string | null {
  const serialized = serializeExpiry(raw, { now: 0 })
  return serialized.ok ? serialized.value : null
}

// ---------------------------------------------------------------------------
// Reversal
// ---------------------------------------------------------------------------

type ReversalPath = 'virtual' | 'monetary'

interface ReversalPayloadSnapshot {
  path: ReversalPath
  amount: string
  reason: string
  /** Path B only: the bucket debited, and how to render its amount. */
  subAccountId: string | null
  bucketLabel: string | null
  asset: { asset_kind: string; currency: string | null } | null
}

export interface ReversalDialogProps extends WriteDialogProps {
  /** Every `active` bucket the account holds — Path B's candidate universe. */
  activeBuckets: readonly SubAccountRow[]
}

export function ReversalDialog(props: ReversalDialogProps) {
  const { open, applicationId, accountId, activeBuckets, onClose, onRefresh, notify } = props
  const { state, setState, reset, controller, endSession, sessionId } = useDialogSession(
    open, applicationId, accountId
  )
  const [path, setPath] = useState<ReversalPath>('virtual')
  const [monetaryId, setMonetaryId] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [droppedNotice, setDroppedNotice] = useState<string | null>(null)
  const locked = useRef<ReversalPayloadSnapshot | null>(null)

  const { catalogue, types, reload: loadCatalogue } = useTypeCatalogue(
    open, sessionId, controller
  )
  const reclaim = useMemo(
    () => buildReclaimTargets(activeBuckets, types),
    [activeBuckets, types]
  )
  const selectedBucket = reclaim.offered.find((target) => target.id === monetaryId) ?? null

  /**
   * Both selection changes that decide the unit go through one handler
   * (§4.5): the path itself, and — on Path B — which bucket is debited.
   */
  const choosePath = useCallback((next: ReversalPath) => {
    setPath(next)
    setAmount('')
    setDroppedNotice(null)
  }, [])
  const chooseBucket = useCallback((next: string) => {
    setMonetaryId(next)
    setAmount('')
    setDroppedNotice(null)
  }, [])

  const close = useCallback(() => {
    endSession()
    setState(closeDialog)
    locked.current = null
    setPath('virtual')
    setMonetaryId('')
    setAmount('')
    setReason('')
    setDroppedNotice(null)
    reset()
    onClose()
  }, [endSession, onClose, reset, setState])

  // Same target binding as the grant dialog: a reversal typed for account A is
  // never submitted against account B after a back/forward navigation.
  useCloseOnTargetChange(applicationId, accountId, close)

  /**
   * A late catalogue only adds or removes Path B options. It never selects one
   * on the operator's behalf and never changes the selected path; a bucket that
   * is no longer offered is deselected, said so, and its amount cleared.
   */
  useEffect(() => {
    if (state.locked) return
    if (!monetaryId) return
    if (reclaim.offered.some((target) => target.id === monetaryId)) return
    setMonetaryId('')
    setAmount('')
    setDroppedNotice('该桶已不再是本弹窗可回收的赠金桶，请另选一个。')
  }, [monetaryId, reclaim, state.locked])

  const send = useCallback(
    async (payload: ReversalPayloadSnapshot, isRetry: boolean) => {
      const token = controller.current()
      if (token === null) return
      if (payload.path === 'monetary') {
        await sendInSession<void>({
          token,
          controller,
          request: (target: SessionTarget) =>
            submitDirectedDebit(
              target.applicationId,
              target.accountId,
              payload.subAccountId as string,
              {
                amount: payload.amount,
                reason: payload.reason,
                idempotencyKey: state.idempotencyKey,
              }
            ),
          onSuccess: () => {
            const result = applyOutcome(state, { kind: 'success' }, { isRetry })
            setState(result.state)
            if (result.effects.includes('success-toast')) {
              notify(
                describeDirectedDebit(
                  payload.bucketLabel ?? payload.subAccountId ?? '',
                  formatAmount(payload.amount, payload.asset ?? {
                    asset_kind: 'virtual', currency: null,
                  })
                )
              )
            }
            if (result.effects.includes('refresh')) onRefresh()
            close()
          },
          onFailure: (err: unknown) => {
            const status = errorStatus(err)
            // A determinate refusal on a FIRST submission only: a 422 answering
            // a retry after an unknown outcome stays on the unknown-outcome
            // message, which `applyOutcome` enforces by taking `isRetry` first.
            const determinateInsufficient =
              !isRetry && status === 422 && errorCode(err) === 'insufficient_balance'
            const outcome = classifyHttpOutcome(
              status,
              determinateInsufficient
                ? DIRECTED_DEBIT_INSUFFICIENT_MESSAGE
                : serverErrorMessage(err, 'Reversal failed')
            )
            setState((current) => applyOutcome(current, outcome, { isRetry }).state)
          },
        })
        return
      }
      await sendInSession<ReversalOutcome>({
        token,
        controller,
        request: (target: SessionTarget) =>
          submitReversal(target.applicationId, target.accountId, {
            amount: payload.amount,
            reason: payload.reason,
            idempotencyKey: state.idempotencyKey,
          }),
        onSuccess: (outcome: ReversalOutcome) => {
          const result = applyOutcome(state, { kind: 'success' }, { isRetry })
          setState(result.state)
          if (result.effects.includes('success-toast')) {
            // Both figures, always — a positive shortfall is never a plain success.
            notify(describeReversal(outcome))
          }
          if (result.effects.includes('refresh')) onRefresh()
          close()
        },
        onFailure: (err: unknown) => {
          const reversalOutcome = classifyHttpOutcome(
            errorStatus(err),
            serverErrorMessage(err, 'Reversal failed')
          )
          setState((current) => applyOutcome(current, reversalOutcome, { isRetry }).state)
        },
      })
    },
    [close, controller, notify, onRefresh, setState, state]
  )

  const submit = useCallback(() => {
    const asset =
      path === 'monetary' && selectedBucket ? targetAsset(selectedBucket) : null
    if (path === 'monetary' && !selectedBucket) {
      setState((current) => failValidation(current, '请先选择要定向回收的 monetary 桶。'))
      return
    }
    const amountCheck = validateForTarget(
      amount,
      asset ?? { asset_kind: 'virtual', currency: null }
    )
    if (!amountCheck.ok) {
      setState((current) => failValidation(current, amountCheck.message))
      return
    }
    if (!reason.trim()) {
      setState((current) => failValidation(current, 'A reason is required.'))
      return
    }
    const payload: ReversalPayloadSnapshot = {
      path,
      amount: amountCheck.value,
      reason: reason.trim(),
      subAccountId: selectedBucket?.id ?? null,
      bucketLabel: selectedBucket
        ? `${selectedBucket.type}（${selectedBucket.currency ?? ''}）`
        : null,
      asset,
    }
    locked.current = payload
    setState(beginSubmit)
    void send(payload, false)
  }, [amount, path, reason, selectedBucket, send, setState])

  const retry = useCallback(() => {
    const payload = locked.current
    if (!payload) return
    setState(beginRetry)
    void send(payload, true)
  }, [send, setState])

  const amountBucket =
    path === 'monetary' && selectedBucket ? targetAsset(selectedBucket) : null

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>回收赠送余额</DialogTitle>
          <DialogDescription>
            本操作针对账户 {accountId}。两条回收路径的作用域、端点与失败语义都不同，
            请显式选择其一。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <fieldset className="space-y-1 text-sm">
            <legend className="mb-1 text-muted-foreground">回收路径</legend>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                className="mt-1"
                name="reversal-path"
                checked={path === 'virtual'}
                onChange={() => choosePath('virtual')}
                disabled={state.locked}
                aria-label="虚拟赠金（账户级）"
              />
              <span>
                虚拟赠金（账户级）：服务端按自身回收顺序，从该账户「可赠送」的虚拟桶中
                尽力回收至多所填金额，由服务端决定消费哪些桶，**不是**撤销某一条流水。
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                className="mt-1"
                name="reversal-path"
                checked={path === 'monetary'}
                onChange={() => choosePath('monetary')}
                disabled={state.locked}
                aria-label="定向回收 monetary 赠金桶"
              />
              <span>
                定向回收 monetary 赠金桶：对所选的那一个桶发起 adjust debit，
                全有或全无，余额不足时不做部分回收。
              </span>
            </label>
          </fieldset>

          {path === 'monetary' && (
            <div className="space-y-2">
              {catalogue.status === 'loading' && (
                <p className="text-sm text-muted-foreground">正在读取该应用的桶类型…</p>
              )}
              {catalogue.status === 'failed' && (
                <p role="alert" className="text-sm text-red-700">
                  {CATALOGUE_FAILED_PREFIX}：{catalogue.message}
                  {' '}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => { void loadCatalogue() }}
                  >
                    重试
                  </button>
                </p>
              )}
              {catalogue.status === 'loaded' && reclaim.offered.length === 0 && (
                <p className="text-sm text-amber-700">
                  该账户没有任何可由本弹窗回收的 monetary 赠金桶
                  （需要是该应用声明为 app_creditable、不可提现、非 cash 类型，
                  且同币种下还有比它更晚消费的桶）。回收只能直接调用管理员 adjust API。
                </p>
              )}
              {droppedNotice && (
                <p role="alert" className="text-sm text-amber-700">{droppedNotice}</p>
              )}
              {reclaim.offered.length > 0 && (
                <label className="block text-sm">
                  <span className="mb-1 block text-muted-foreground">目标 monetary 桶</span>
                  <select
                    className="w-full rounded-md border px-3 py-2"
                    value={monetaryId}
                    onChange={(e) => chooseBucket(e.target.value)}
                    disabled={state.locked}
                    aria-label="目标 monetary 桶"
                  >
                    <option value="">请选择</option>
                    {reclaim.offered.map((target) => (
                      <option key={target.id} value={target.id}>
                        {describeGrantTarget(target)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">
              {path === 'monetary'
                ? amountUnitLabel(amountBucket)
                : '金额（虚拟桶原子单位整数）'}
            </span>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              readOnly={state.locked}
              disabled={path === 'monetary' && selectedBucket === null}
              inputMode="decimal"
              aria-label="金额"
            />
            {path === 'monetary' && selectedBucket && amount !== '' && (() => {
              const check = validateForTarget(amount, targetAsset(selectedBucket))
              return check.ok ? (
                <span className="mt-1 block text-xs text-muted-foreground">
                  {describeWireAmount(check.value, targetAsset(selectedBucket))}
                </span>
              ) : null
            })()}
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">原因（必填，写入流水）</span>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              readOnly={state.locked}
              aria-label="原因"
            />
          </label>
        </div>

        {state.message && (
          <p role="alert" className="text-sm text-red-700">{state.message}</p>
        )}

        <DialogFooter>
          <OutcomeControls
            state={state}
            onRetry={retry}
            onClose={close}
            onSubmit={submit}
            submitLabel="确认回收"
            submitDisabled={path === 'monetary' && selectedBucket === null}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
